import { api, getErrorMessage } from "./client";
import { paystackCreateBankTransfer } from "./paystack";
import { getExternalErrorMessage } from "./externalClient";

const FEE = 50;
const TOTAL_TIME_SECONDS = 15 * 60;

export type BankTransferAccount = {
  accountNumber: string;
  bankName: string;
  accountName: string;
  fee: number;
  expiresAt?: string;
  reference?: string;
};

function expiresAtISO() {
  return new Date(Date.now() + TOTAL_TIME_SECONDS * 1000).toISOString();
}

export async function createBankTransferAccount(payload: {
  amount: number;
  email: string;
  mode?: "backend" | "paystack";
  metadata?: Record<string, unknown>;
}): Promise<BankTransferAccount> {
  const mode = payload.mode ?? "backend";

  const amountToReceive = payload.amount + FEE;

  if (mode === "paystack") {
    try {
      const res = await paystackCreateBankTransfer({
        email: payload.email,
        amountKobo: Number(amountToReceive) * 100,
        expiresAtISO: expiresAtISO(),
        metadata: payload.metadata,
      });

      if (!res?.status) throw new Error(res?.message || "Failed to generate Paystack account");

      const d = res.data;

      return {
        accountNumber: d?.account_number,
        bankName: d?.bank?.name,
        accountName: d?.account_name,
        fee: FEE,
        expiresAt: d?.account_expires_at,
        reference: d?.reference,
      };
    } catch (e: any) {
      throw new Error(getExternalErrorMessage(e));
    }
  }

  try {
    const res = await api.post(`/payments/bank-transfer`, {
      amount: payload.amount,
      email: payload.email,
      expiresInSeconds: TOTAL_TIME_SECONDS,
      fee: FEE,
    });

    const d = res.data?.data;

    return {
      accountNumber: d?.accountNumber || d?.account_number,
      bankName: d?.bankName || d?.bank?.name,
      accountName: d?.accountName || d?.account_name,
      fee: d?.fee ?? FEE,
      expiresAt: d?.expiresAt || d?.account_expires_at,
      reference: d?.reference || d?.tx_ref || d?.ref,
    };
  } catch (e: any) {
    throw new Error(getErrorMessage(e));
  }
}

export async function verifyBankTransfer(payload: {
  userId: string;
  reference: string;
}) {
  const res = await api.get(`/checkAddFundStatus`, {
    params: {
      userId: payload.userId,
      tx_ref: payload.reference,
    },
    headers: {
      "x-no-loader": "1",
    },
  });

  return res.data;
}
